import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  PageLevelError, ThemedProgressCircle,
} from '@americanexpress/consumer-travel-base';
import { createStructuredSelector } from 'reselect';
import { withRouter } from '@americanexpress/one-app-router';
import { RenderModule } from 'holocron';
import { MODULE_NAME, MODULE_NAME_SEARCH_REDIRECT } from '../constants/packagesConstants';
import PackagesSearchBody from './PackagesSearchBody/PackagesSearchBody';
import themeContext from '../context/themeContext';
import { getRequest } from '../selectors/requestSelectors';
import loadCacheThunk from '../thunks/loadCacheThunk';
import savePackagesCacheThunk from '../thunks/savePackagesCacheThunk';
import packageContext from '../context/packageContext';
import { getPropOrDefault } from '../utils/marketConfigUtils';
import { getMarketConfig } from '../selectors/marketConfigSelector';
import ErrorsWarning from './ErrorsWarning/ErrorsWarning';
import { createInav } from '../utils/createINav';

const TravelPackages = ({
  id,
  moduleLoadStatus,
  theme,
  loadCache,
  savePackagesCache,
  request,
  maxAdults,
  maxChildren,
  maxRooms,
  marketConfig,
  location,
}) => {
  const moduleWrapperRef = React.useRef(null);
  const { pathname } = location;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  useMemo(() => createInav(pathname, dispatch), [pathname, dispatch]);

  useEffect(() => {
    loadCache();
  }, []);

  useEffect(() => {
    savePackagesCache();
  }, [request]);

  if (moduleLoadStatus === 'error') {
    return <PageLevelError description={formatMessage({ id: 'error.module_load' })} id={id} />;
  }

  if (moduleLoadStatus === 'loading') {
    return (
      <div className="flex flex-justify-center pad-3" id={id} data-testid="loadingState">
        <ThemedProgressCircle theme={theme} size="lg" />
      </div>
    );
  }

  const {
    MIN_NIGHTS,
    MIN_NIGHTS_AIR_CAR,
    MAX_NIGHTS,
    MAX_ADULTS,
    MAX_CHILDREN,
    MAX_INFANTS,
    MAX_COMBINED_CHILDREN_AND_INFANTS,
    MAX_ROOMS,
    MAX_ADVANCE_BOOKING,
    MIN_ADVANCE_AIR_CAR_BOOKING,
    MIN_ADVANCE_AIR_HOTEL_BOOKING,
    MIN_ADVANCE_AIR_HOTEL_CAR_BOOKING,
  } = marketConfig;

  return (
    <themeContext.Provider value={theme}>
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- Ignore */}
      <packageContext.Provider value={{
        state: {
          moduleWrapperRef,
        },
        config: {
          minDays: MIN_NIGHTS,
          minDaysAirCar: MIN_NIGHTS_AIR_CAR,
          maxDays: MAX_NIGHTS,
          maxAdults: getPropOrDefault(maxAdults, MAX_ADULTS),
          maxChildren: getPropOrDefault(maxChildren, MAX_CHILDREN),
          maxInfants: MAX_INFANTS,
          maxCombinedChildrenAndInfants: MAX_COMBINED_CHILDREN_AND_INFANTS,
          maxRooms: getPropOrDefault(maxRooms, MAX_ROOMS),
          maxAdvanceBooking: MAX_ADVANCE_BOOKING,
          minAdvanceAirCarBooking: MIN_ADVANCE_AIR_CAR_BOOKING,
          minAdvanceAirHotelBooking: MIN_ADVANCE_AIR_HOTEL_BOOKING,
          minAdvanceAirHotelCarBooking: MIN_ADVANCE_AIR_HOTEL_CAR_BOOKING,
        },
      }}
      >
        <div
          id={id}
          ref={moduleWrapperRef}
          data-testid="travelPackages"
        >
          <ErrorsWarning />
          <PackagesSearchBody />
          <RenderModule moduleName={MODULE_NAME_SEARCH_REDIRECT} />
        </div>
      </packageContext.Provider>
    </themeContext.Provider>
  );
};

TravelPackages.propTypes = {
  id: PropTypes.string,
  moduleLoadStatus: PropTypes.string.isRequired,
  theme: PropTypes.string,
  request: PropTypes.shape({}).isRequired,
  loadCache: PropTypes.func.isRequired,
  savePackagesCache: PropTypes.func.isRequired,
  maxAdults: PropTypes.number,
  maxChildren: PropTypes.number,
  maxRooms: PropTypes.number,
  marketConfig: PropTypes.shape({
    MIN_NIGHTS: PropTypes.number.isRequired,
    MIN_NIGHTS_AIR_CAR: PropTypes.number.isRequired,
    MAX_NIGHTS: PropTypes.number.isRequired,
    MAX_ADULTS: PropTypes.number.isRequired,
    MAX_CHILDREN: PropTypes.number.isRequired,
    MAX_INFANTS: PropTypes.number.isRequired,
    MAX_COMBINED_CHILDREN_AND_INFANTS: PropTypes.number.isRequired,
    MAX_ROOMS: PropTypes.number.isRequired,
    MAX_ADVANCE_BOOKING: PropTypes.number.isRequired,
    MIN_ADVANCE_AIR_CAR_BOOKING: PropTypes.string.isRequired,
    MIN_ADVANCE_AIR_HOTEL_BOOKING: PropTypes.string.isRequired,
    MIN_ADVANCE_AIR_HOTEL_CAR_BOOKING: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

TravelPackages.defaultProps = {
  maxAdults: null,
  maxChildren: null,
  maxRooms: null,
};

TravelPackages.defaultProps = {
  id: MODULE_NAME,
};

const mapStateToProps = createStructuredSelector({
  request: getRequest,
  marketConfig: getMarketConfig,
});

const mapDispatchToProps = {
  loadCache: loadCacheThunk,
  savePackagesCache: savePackagesCacheThunk,
};

const hocChain = compose(
  connect(mapStateToProps, mapDispatchToProps)
);

export { TravelPackages as TestableTravelPackages };

export default hocChain(withRouter(TravelPackages));
